import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { companyInfo } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const prefill = location.state?.prefill || null;

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: prefill?.subject || '',
    message: prefill?.message || '',
    website: ''
  });

  const [sampleForm, setSampleForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: prefill?.product || '',
    city: '',
    message: ''
  });

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSampleChange = (e) => {
    setSampleForm({
      ...sampleForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success('Message Sent!', {
        description: response.data.message
      });
      setContactForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly via phone.'
      });
    }
  };

  const handleSampleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/sample-request`, sampleForm);
      toast.success('Sample Request Received!', {
        description: response.data.message
      });
      setSampleForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        product: '',
        city: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting sample request:', error);
      toast.error('Failed to submit request', {
        description: 'Please try again or contact us directly.'
      });
    }
  };

  const contactCards = [
    {
      icon: Phone,
      title: 'Phone',
      content: companyInfo.phone,
      href: `tel:${companyInfo.phone}`
    },
    {
      icon: Mail,
      title: 'Email',
      content: companyInfo.email,
      href: `mailto:${companyInfo.email}`
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM',
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Get in Touch</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Contact Us
            </h1>
            <p className="text-xl text-white/70 max-w-xl">
              Get in touch with us for product inquiries, samples, or any questions about our manufacturing capabilities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-8 -mt-10 relative z-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {contactCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {card.href ? (
                  <a
                    href={card.href}
                    className="block p-6 bg-white border border-black/5 hover:border-[#D4AF37]/30 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#004534] group-hover:bg-[#D4AF37] transition-colors">
                        <card.icon className="h-6 w-6 text-[#D4AF37] group-hover:text-[#004534] transition-colors" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#004534] mb-1">{card.title}</h3>
                        <p className="text-[#525956] text-sm break-all">{card.content}</p>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div className="p-6 bg-white border border-black/5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#004534]">
                        <card.icon className="h-6 w-6 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#004534] mb-1">{card.title}</h3>
                        <p className="text-[#525956] text-sm">{card.content}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="contact" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-transparent gap-4 p-0 h-auto">
                <TabsTrigger 
                  value="contact" 
                  className="py-4 px-6 bg-white border border-black/10 data-[state=active]:bg-[#004534] data-[state=active]:text-white data-[state=active]:border-[#004534] transition-all"
                >
                  General Inquiry
                </TabsTrigger>
                <TabsTrigger 
                  value="sample" 
                  className="py-4 px-6 bg-white border border-black/10 data-[state=active]:bg-[#004534] data-[state=active]:text-white data-[state=active]:border-[#004534] transition-all"
                >
                  Request Sample
                </TabsTrigger>
              </TabsList>

              <TabsContent value="contact">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white border border-black/5 p-8 lg:p-12"
                >
                  <div className="mb-8">
                    <p className="label-mono text-[#D4AF37] mb-2">Inquiry Form</p>
                    <h2 className="text-2xl font-bold text-[#004534] font-['Outfit']">Send Us a Message</h2>
                    <p className="text-[#525956] mt-2">Have questions? We'd love to hear from you</p>
                  </div>

                  {prefill && (
                    <div className="mb-8 p-4 bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] flex items-start gap-3" data-testid="prefill-banner">
                      <Sparkles className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#004534]">
                        Pre-filled from your AI recommendation: <strong>{prefill.product}</strong>. Just add your name and contact details, then hit send!
                      </p>
                    </div>
                  )}
                  
                  <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                    <input type="text" name="website" value={contactForm.website} onChange={handleContactChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_name" className="text-[#004534] font-medium">Name *</Label>
                        <Input
                          id="contact_name"
                          name="name"
                          type="text"
                          value={contactForm.name}
                          onChange={handleContactChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email" className="text-[#004534] font-medium">Email *</Label>
                        <Input
                          id="contact_email"
                          name="email"
                          type="email"
                          value={contactForm.email}
                          onChange={handleContactChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_phone" className="text-[#004534] font-medium">Phone</Label>
                        <Input
                          id="contact_phone"
                          name="phone"
                          type="tel"
                          value={contactForm.phone}
                          onChange={handleContactChange}
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-[#004534] font-medium">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          value={contactForm.subject}
                          onChange={handleContactChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact_message" className="text-[#004534] font-medium">Message *</Label>
                      <Textarea
                        id="contact_message"
                        name="message"
                        rows={5}
                        value={contactForm.message}
                        onChange={handleContactChange}
                        required
                        className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        data-testid="contact-message"
                      />
                    </div>

                    <motion.button
                      type="submit"
                      className="w-full py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      data-testid="contact-submit"
                    >
                      Send Message
                      <ArrowRight className="h-4 w-4" />
                    </motion.button>
                  </form>
                </motion.div>
              </TabsContent>

              <TabsContent value="sample">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white border border-black/5 p-8 lg:p-12"
                >
                  <div className="mb-8">
                    <p className="label-mono text-[#D4AF37] mb-2">Sample Request</p>
                    <h2 className="text-2xl font-bold text-[#004534] font-['Outfit']">Request Product Sample</h2>
                    <p className="text-[#525956] mt-2">Get samples delivered to evaluate our quality</p>
                  </div>
                  
                  <form onSubmit={handleSampleSubmit} className="space-y-6" data-testid="sample-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="sample_name" className="text-[#004534] font-medium">Name *</Label>
                        <Input
                          id="sample_name"
                          name="name"
                          type="text"
                          value={sampleForm.name}
                          onChange={handleSampleChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sample_email" className="text-[#004534] font-medium">Email *</Label>
                        <Input
                          id="sample_email"
                          name="email"
                          type="email"
                          value={sampleForm.email}
                          onChange={handleSampleChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="sample_phone" className="text-[#004534] font-medium">Phone *</Label>
                        <Input
                          id="sample_phone"
                          name="phone"
                          type="tel"
                          value={sampleForm.phone}
                          onChange={handleSampleChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-[#004534] font-medium">Company/Business Name</Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={sampleForm.company}
                          onChange={handleSampleChange}
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="product" className="text-[#004534] font-medium">Product Interest *</Label>
                        <Input
                          id="product"
                          name="product"
                          type="text"
                          placeholder="e.g., Acrylic Boards, CNC Doors"
                          value={sampleForm.product}
                          onChange={handleSampleChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sample_city" className="text-[#004534] font-medium">City *</Label>
                        <Input
                          id="sample_city"
                          name="city"
                          type="text"
                          value={sampleForm.city}
                          onChange={handleSampleChange}
                          required
                          className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="sample_message" className="text-[#004534] font-medium">Additional Requirements</Label>
                      <Textarea
                        id="sample_message"
                        name="message"
                        rows={4}
                        placeholder="Specify quantity, specific designs, or any other requirements..."
                        value={sampleForm.message}
                        onChange={handleSampleChange}
                        className="mt-2 border-black/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                    </div>

                    <motion.button
                      type="submit"
                      className="w-full py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      data-testid="sample-submit"
                    >
                      Request Sample
                      <ArrowRight className="h-4 w-4" />
                    </motion.button>
                  </form>
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Address Section */}
      <section className="py-16 lg:py-24 bg-[#004534] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] mx-auto mb-6">
                <MapPin className="h-8 w-8 text-[#004534]" />
              </div>
              <p className="label-mono text-[#D4AF37] mb-4">Visit Us</p>
              <h2 className="text-2xl font-bold text-white mb-4 font-['Outfit']">Our Location</h2>
              <p className="text-white/70 text-lg">{companyInfo.address}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
